import { map } from 'lodash';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { numericInputStringToNumber } from '../../../helpers/stringHelpers';
import {
  CreateUpdateDesignManifoldDetailsDto,
  mapDesignManifoldDetailsFormModelToDto,
} from '../../designs/createUpdateDesignManifoldDetailsDto';
import { ManifoldFormModel, RoomlessManifoldFormModel } from '../quote-or-design-form/manifolds/manifoldFormModel';
import { FloorLevelCode, PumpLocationCode, ThermostatTypeCode } from '../quote-or-design-form/quoteOrDesignFormData';
import { RoomFormModel } from '../quote-or-design-form/rooms/roomFormModel';
import {
  CreateUpdateRoomDto,
  DesignCreateUpdateRoomDto,
  mapRoomFormModelToDesignDto,
  mapRoomFormModelToQuoteDto,
  QuoteCreateUpdateRoomDto,
} from '../rooms/createUpdateRoomDto';

export type QuoteCreateUpdateManifoldDto = CreateUpdateManifoldDto<QuoteCreateUpdateRoomDto>;

export type DesignCreateUpdateManifoldDto = CreateUpdateManifoldDto<DesignCreateUpdateRoomDto> & {
  designManifoldDetails: CreateUpdateDesignManifoldDetailsDto | null;
};

export type CreateUpdateManifoldDto<TRoomDto extends CreateUpdateRoomDto> = {
  manifoldId: number | null;
  name: string;
  displayIndex: number;
  isRadiator: boolean;
  pumpPartId: number | null;
  roomlessManifoldDetails: CreateUpdateRoomlessManifoldDetailsDto | null;
  rooms: Array<TRoomDto> | null;
};

export const mapManifoldFormModelToQuoteDto = (
  formModel: ManifoldFormModel,
  manifoldIndex: number,
  pumpLocation: PumpLocationCode,
): QuoteCreateUpdateManifoldDto =>
  mapManifoldFormModelToDto(formModel, manifoldIndex, pumpLocation, mapRoomFormModelToQuoteDto);

export const mapManifoldFormModelToDesignDto = (
  formModel: ManifoldFormModel,
  manifoldIndex: number,
  pumpLocation: PumpLocationCode,
): DesignCreateUpdateManifoldDto => ({
  ...mapManifoldFormModelToDto(formModel, manifoldIndex, pumpLocation, mapRoomFormModelToDesignDto),
  designManifoldDetails: mapDesignManifoldDetailsFormModelToDto(assertNotNull(formModel.designManifoldDetails)),
});

const mapManifoldFormModelToDto = <TRoomDto extends CreateUpdateRoomDto>(
  formModel: ManifoldFormModel,
  manifoldIndex: number,
  pumpLocation: PumpLocationCode,
  mapRoomFormModelToDto: (roomFormModel: RoomFormModel, roomIndex: number, isRadiator: boolean) => TRoomDto,
): CreateUpdateManifoldDto<TRoomDto> => {
  return {
    manifoldId: formModel.manifoldId,
    name: formModel.name,
    displayIndex: manifoldIndex,
    isRadiator: formModel.isRadiator,
    pumpPartId: formModel.pump == null ? null : formModel.pump.partId,
    roomlessManifoldDetails:
      formModel.roomlessManifold != null ? mapRoomlessManifoldFormModelToDto(formModel.roomlessManifold) : null,
    rooms:
      formModel.rooms != null
        ? map(formModel.rooms, (room, roomIndex) => mapRoomFormModelToDto(room, roomIndex, formModel.isRadiator))
        : null,
  };
};

export type CreateUpdateRoomlessManifoldDetailsDto = {
  floorLevelCode: FloorLevelCode | null;
  floorConstructionPartId: number | null;
  areaInMetresSquared: number | null;
  thermostatTypeCode: ThermostatTypeCode;
  numberOfThermostats: number | null;
  numberOfWetSensors: number | null;
  numberOfFloorSensors: number | null;
  numberOfPorts: number | null;
};

export const mapRoomlessManifoldFormModelToDto = (
  formModel: RoomlessManifoldFormModel,
): CreateUpdateRoomlessManifoldDetailsDto => {
  return {
    floorLevelCode: formModel.floorLevelCode,
    floorConstructionPartId: formModel.floorConstruction?.partId ?? null,
    areaInMetresSquared: numericInputStringToNumber(formModel.areaInMetresSquared),
    thermostatTypeCode: formModel.thermostatTypeCode,
    numberOfThermostats: numericInputStringToNumber(formModel.numberOfThermostats),
    numberOfWetSensors: numericInputStringToNumber(formModel.numberOfWetSensors),
    numberOfFloorSensors: numericInputStringToNumber(formModel.numberOfFloorSensors),
    numberOfPorts: numericInputStringToNumber(formModel.numberOfPorts),
  };
};
